/**
 * EndCommunityLiveStreamUseCase
 *
 * Closes the community live session: merge the `type = live` community message to
 * `isLive: false` (carrying the friendly end text + updated timestamp) AND write
 * the end `liveStarted` notification (`read: true`), in one transactional write
 * via `LiveStreamingService.endCommunityLiveStream`. Fired once by the Task-10
 * live-streaming hook on the broadcaster's page-dispose with the computed live
 * duration.
 *
 * Web analog of Flutter's `community_screen.endLiveMessage`
 * (lib/presentation/screens/community/community_screen.dart:641-698), minus the
 * two presentation-side guards Flutter runs first:
 *   - `firebaseUser == null` -> `false` (`:647-650`)
 *   - the optimistic local-list flip (`message.broadcastingInfo!.isLive = false`,
 *     `:653-659`) is a presentation concern: on web the community PAGE owns it
 *     via `useCommunity.markLiveMessageEnded`, invoked on the broadcaster's
 *     pop-back to this wall (`CommunityPage` `onPopNext`). The keep-alive
 *     community page does not re-render the broadcaster's own tile off this end
 *     write's listener echo, so the flip is done locally there (see finding 1).
 *   - `!_internetConnectionActive` -> toast + `false` (`:661-664`)
 * On web those live in the hook/page (which owns toasts + the auth read), NOT in
 * this pure-domain use case.
 *
 * Parity notes on the built entities (`community_screen.dart:666-689`):
 *   - `timestamp = Date.now()` (`:666`), shared by the message update
 *     (`updatedTimestamp`), the notification `info.timestamp`, and the
 *     notification `info.broadcastingInfo.timestamp`.
 *   - `usersInfo = { userId: broadcasterFirebaseId, name: broadcasterName }`
 *     (`:667-670`) — the current firebase uid + the wall owner's name.
 *   - `broadcastingInfo = { liveStreamingMessageId, timestamp, isLive: false }`
 *     (`:672-676`); `broadcasterStreamingId` is left undefined -> the Task-4
 *     `toJson` emits `-1` (Flutter's `BroadcastingInfo` default).
 *   - `notificationType = AppNotificationType.liveStarted` — Flutter's
 *     `endLiveMessage` reuses `liveStarted.index`, NOT a distinct `liveEnded`
 *     value (`:684`); ported faithfully.
 *   - `read = true` (`:683`) — the end notification is written already-read.
 *   - `notification.id = <uid>_live` via the shared `LIVE_NOTIFICATION_SUFFIX`
 *     (`:688`, `AppConstants.liveNotificationSuffix`).
 *   - `endText` is the pre-formatted `"{name} was live for {duration}"` string
 *     (`:694`); the web builds the friendly duration via
 *     `useDateFormatters().formatCallDuration(liveDurationMs)` in the
 *     presentation layer — it is l10n-driven, so it cannot live in a pure-domain
 *     use case.
 *
 * Constructor-injected service (video-call use-case pattern) for testability.
 */

import { AppNotificationType } from '@/data/enums/notification/appNotificationType';
import { LIVE_NOTIFICATION_SUFFIX } from '@/domain/constants/notificationConstants';
import {
  LiveStreamingService,
  liveStreamingService,
} from '@/domain/services/liveStreaming/liveStreamingService';
import type { AppNotificationEntity } from '@/domain/entities/notification/appNotificationEntity';

export interface EndCommunityLiveStreamParams {
  /**
   * The broadcaster's Firebase uid — the current firebase uid AND the notification
   * usersInfo id (`community_screen.dart:669`). Also forms the `<uid>_live`
   * notification id (`:688`).
   */
  broadcasterFirebaseId: string;
  /** The broadcaster's display name (notification usersInfo name, `:670`). */
  broadcasterName: string;
  /** The live message `id` (the doc merged to `isLive: false`, `:674`). */
  liveStreamingMessageId: string;
  /**
   * The pre-formatted `"{name} was live for {duration}"` end text built by the
   * Task-10 hook (`:694`; l10n-driven friendly duration lives in presentation).
   */
  endText: string;
}

/**
 * Params for the unload-safe end path (`executeOnDispose`). No `broadcasterName`
 * — that field only feeds the notification's `usersInfo.name`, and the unload
 * path skips the notification write entirely (finding_5 step 2).
 */
export interface EndCommunityLiveStreamOnDisposeParams {
  /**
   * The broadcaster's Firebase uid == the wall `owner` == the live message doc's
   * parent user. The data layer verifies it against the current auth uid.
   */
  broadcasterFirebaseId: string;
  /** The live message `id` (== its Firestore doc id; merged to `isLive: false`). */
  liveStreamingMessageId: string;
  /**
   * The pre-formatted `"{name} was live for {duration}"` end text (l10n-driven
   * friendly duration built in presentation).
   */
  endText: string;
}

export class EndCommunityLiveStreamUseCase {
  private service: LiveStreamingService;

  constructor(service: LiveStreamingService = liveStreamingService) {
    this.service = service;
  }

  /**
   * Build the end `liveStarted` notification and hand the message merge + the
   * notification to the service for one transactional write.
   *
   * @returns Promise<boolean> — the service's transactional-write result,
   *   propagated verbatim (parity `community_screen.dart:691-697`).
   */
  async execute(params: EndCommunityLiveStreamParams): Promise<boolean> {
    const { broadcasterFirebaseId, broadcasterName, liveStreamingMessageId, endText } = params;

    // Shared across the message merge + notification (community_screen.dart:666).
    const timestamp = Date.now();

    // The end `liveStarted` notification (community_screen.dart:667-689). Its id is
    // the deterministic `<uid>_live` (LIVE_NOTIFICATION_SUFFIX), `read = true`, and
    // `broadcasterStreamingId` is left undefined (the Task-4 toJson emits -1).
    const notification: AppNotificationEntity = {
      id: broadcasterFirebaseId + LIVE_NOTIFICATION_SUFFIX,
      info: {
        usersInfo: {
          userId: broadcasterFirebaseId,
          name: broadcasterName,
        },
        broadcastingInfo: {
          liveStreamingMessageId,
          timestamp,
          isLive: false,
        },
        timestamp,
        read: true,
        notificationType: AppNotificationType.liveStarted,
      },
    };

    return this.service.endCommunityLiveStream({
      messageId: liveStreamingMessageId,
      text: endText,
      updatedTimestamp: timestamp,
      notification,
    });
  }

  /**
   * Unload-safe end path for the broadcaster's page-dispose / tab-kill. Flips the
   * live message to `isLive: false` via a single lightweight `setDoc` merge
   * (no transaction, no server read, no notification) so the write reliably
   * flushes during `beforeunload` — finding_5.
   *
   * WHY A SECOND METHOD (web-specific divergence). `execute` above writes the
   * message merge AND the `liveEnded` notification in one Firestore transaction;
   * that read-then-commit (preceded by a collection-group lookup) does not land
   * when the browser tears the page down on tab-kill, leaving the message
   * `isLive: true` forever. This path drops the transaction, the pre-query and the
   * secondary notification write, keeping ONLY the critical `isLive: false` flip
   * on the directly-known `users/{owner}/community_messages/{messageId}` doc. The
   * in-session (SPA-nav) end keeps the transactional `execute` for full parity;
   * only the unload path uses this. Flutter (mobile) disposes via the app
   * lifecycle where the transaction completes normally — do not change Flutter.
   *
   * The message merge payload is byte-identical to `execute`'s
   * (`{ text, updatedTimestamp, broadcastingInfo: { isLive: false } }`,
   * `community_data_source.dart:236-244`); only the persistence strategy differs.
   *
   * @returns Promise<boolean> — the service's single-write result, propagated
   *   verbatim. Fired fire-and-forget (never awaited before Agora teardown) by the
   *   dispose hook (finding_5 step 3), so the caller does not block on it.
   */
  async executeOnDispose(params: EndCommunityLiveStreamOnDisposeParams): Promise<boolean> {
    const { broadcasterFirebaseId, liveStreamingMessageId, endText } = params;

    return this.service.endCommunityLiveStreamByOwner({
      owner: broadcasterFirebaseId,
      messageId: liveStreamingMessageId,
      text: endText,
      updatedTimestamp: Date.now(),
    });
  }
}

// Export singleton instance
export const endCommunityLiveStreamUseCase = new EndCommunityLiveStreamUseCase();
